import { useState, useCallback, useEffect, useRef } from 'react';
import { TableColumn } from 'react-data-table-component';
import { Input, Button, Badge, Dropdown, DropdownToggle, DropdownMenu, Table } from 'reactstrap';
import { BottomSheet } from '../modal/BottomSheet';

export interface FilterValue {
	[key: string]: string | number | boolean | null;
}

export interface FilterConfig {
	field: string;
	operator: 'equals' | 'contains' | 'greaterThan' | 'lessThan' | 'between';
	value: string | number | null;
	value2?: string | number | null;
}

interface AdvancedFilterProps<T> {
	columns: TableColumn<T>[];
	onApplyFilters: (filters: FilterConfig[]) => void;
	onClearFilters: () => void;
	isOpen: boolean;
	onToggle: () => void;
	activeFiltersCount?: number;
	initialFilters?: FilterConfig[];
}

export const AdvancedFilter = <T,>({
	columns,
	onApplyFilters,
	onClearFilters,
	isOpen,
	onToggle,
	activeFiltersCount = 0,
	initialFilters = [],
}: AdvancedFilterProps<T>) => {
	const [filters, setFilters] = useState<FilterConfig[]>(initialFilters);
	const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

	useEffect(() => {
		const handleResize = () => setIsMobile(window.innerWidth < 768);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	// Sync local filters with initialFilters when dropdown opens
	useEffect(() => {
		if (isOpen) {
			setFilters(initialFilters);
		}
	}, [isOpen, initialFilters]);

	// Filtrar columnas y asegurar que name sea string
	const filterableColumns = columns
		.filter(
			(col) => col.sortable !== false && !col.sortField?.includes('actions')
		)
		.filter((col) => typeof col.name === 'string' && col.name.trim() !== '')
		.map((col) => ({
			name: col.name as string,
			field: (col.sortField || col.name) as string,
		}));

	const addFilter = useCallback(() => {
		if (filterableColumns.length === 0) {
			console.warn('[v0] No filterable columns available');
			return;
		}

		setFilters([
			...filters,
			{
				field: filterableColumns[0].field,
				operator: 'contains',
				value: null,
			},
		]);
	}, [filters, filterableColumns]);

	const removeFilter = useCallback(
		(index: number) => {
			setFilters(filters.filter((_, i) => i !== index));
		},
		[filters]
	);

	const updateFilter = useCallback(
		(index: number, updates: Partial<FilterConfig>) => {
			const newFilters = [...filters];
			newFilters[index] = { ...newFilters[index], ...updates };
			setFilters(newFilters);
		},
		[filters]
	);

	const handleApply = () => {
		onApplyFilters(filters.filter((f) => f.value !== null && f.value !== ''));
		onToggle();
	};

	const handleClear = () => {
		setFilters([]);
		onClearFilters();
	};

	if (isMobile) {
		return (
			<>
				<Button
					color="none"
					className={`d-flex align-items-center btn-sm fw-medium ${activeFiltersCount > 0 ? 'text-white' : 'text-primary'}`}
					onClick={onToggle}
					style={{
						backgroundColor: activeFiltersCount > 0 ? '#0766ad' : 'rgba(7, 102, 173, 0.06)',
						border: activeFiltersCount > 0 ? '1px solid #0766ad' : '1px solid rgba(7, 102, 173, 0.15)',
						borderRadius: '8px',
						height: '34px',
						padding: '0 12px',
						fontSize: '0.8rem',
						boxShadow: '0 2px 4px rgba(7, 102, 173, 0.04)',
						transition: 'all 0.2s ease',
						fontWeight: 600,
						display: 'inline-flex',
						alignItems: 'center',
						justifyContent: 'center'
					}}
				>
					<i className={`fa fa-filter me-2 ${activeFiltersCount > 0 ? 'text-white' : 'text-primary'}`} style={{ fontSize: '0.85rem' }}></i>
					Filtros
					{activeFiltersCount > 0 && (
						<Badge color="none" className="ms-2 px-2 py-0.5 rounded-pill" style={{ fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#ffffff', color: '#0766ad' }}>
							{activeFiltersCount}
						</Badge>
					)}
					<i className={`fa fa-chevron-down ms-2 ${activeFiltersCount > 0 ? 'text-white' : 'text-primary'}`} style={{ fontSize: '0.65rem', opacity: 0.8 }}></i>
				</Button>

				<BottomSheet
					isOpen={isOpen}
					onClose={onToggle}
					height="50vh"
					maxHeight="50vh"
				>
					<div className="mobile-bottom-sheet-filters w-100 h-100 pt-2">
						{/* Header bar */}
						<div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between mb-3 border-bottom pb-2 gap-2">
							<div className="d-flex align-items-center justify-content-between">
								<h6 className="mb-0 fw-bold text-dark d-flex align-items-center" style={{ fontSize: '0.9rem' }}>
									Filtros Avanzados de Búsqueda
								</h6>
								<button
									type="button"
									className="btn-close d-md-none shadow-none"
									onClick={onToggle}
									aria-label="Cerrar"
									style={{ fontSize: '0.7rem', padding: '0.25rem' }}
								/>
							</div>
							
							{/* Compact Controls Group */}
							<div className="d-flex align-items-center justify-content-between justify-content-md-end gap-2 flex-wrap">
								<Button
									size="sm"
									color="secondary"
									outline
									onClick={addFilter}
									disabled={filterableColumns.length === 0}
									className="py-1 px-2 d-flex align-items-center justify-content-center flex-fill flex-md-grow-0"
									style={{ fontSize: '0.75rem' }}
								>
									<i className="fa fa-plus me-2"></i> Agregar
								</Button>
								<Button
									size="sm"
									color="primary"
									onClick={handleApply}
									disabled={filters.length === 0}
									className="py-1 px-2 d-flex align-items-center justify-content-center flex-fill flex-md-grow-0 text-white"
									style={{ 
										fontSize: '0.75rem', 
										color: '#ffffff', 
										opacity: filters.length === 0 ? 0.65 : 1 
									}}
								>
									<span style={{ color: '#ffffff' }} className="d-flex align-items-center text-white justify-content-center">
										<i className="fa fa-check me-2" style={{ color: '#ffffff' }}></i> Aplicar
									</span>
								</Button>
								<Button
									size="sm"
									color="warning"
									outline
									onClick={handleClear}
									className="py-1 px-2 d-flex align-items-center justify-content-center flex-fill flex-md-grow-0"
									style={{ fontSize: '0.75rem' }}
								>
									<i className="fa fa-undo me-2"></i> Limpiar
								</Button>
								<button
									type="button"
									className="btn-close d-none d-md-block ms-2 shadow-none"
									onClick={onToggle}
									aria-label="Cerrar"
									style={{ fontSize: '0.7rem', padding: '0.25rem' }}
								/>
							</div>
						</div>

						{/* Filters List Area */}
						<div className="mb-1" style={{ maxHeight: 'calc(50vh - 120px)', overflowY: 'auto', overflowX: 'hidden', paddingRight: '4px', paddingTop: '16px' }}>
							{filters.length === 0 ? (
								<p className="text-muted small text-center my-3" style={{ fontSize: '0.8rem' }}>
									No hay filtros activos. Haz clic en <strong>Agregar</strong> para comenzar.
								</p>
							) : (
								<Table borderless size="sm" className="align-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
									<thead className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
										<tr>
											<th className="pb-2 border-0" style={{ width: '25%', paddingLeft: '8px' }}>Campo</th>
											<th className="pb-2 border-0" style={{ width: '25%', paddingLeft: '8px' }}>Condición</th>
											<th className="pb-2 border-0" style={{ width: '42%', paddingLeft: '8px' }}>Valor / Rango</th>
											<th className="pb-2 border-0 text-center" style={{ width: '8%' }}></th>
										</tr>
									</thead>
									<tbody>
										{filters.map((filter, index) => {
											const selectedCol = filterableColumns.find((c) => c.field === filter.field);
											const isDateField = selectedCol 
												? selectedCol.field.toLowerCase().includes('date') || selectedCol.name.toLowerCase().includes('fecha')
												: false;

											return (
												<tr key={index}>
													<td className="py-1 border-0" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
														<select
															className="form-select form-select-sm border-secondary-subtle"
															value={filter.field}
															onChange={(e) => updateFilter(index, { field: e.target.value })}
															style={{ fontSize: '0.8rem' }}
														>
															{filterableColumns.map((col) => (
																<option key={col.field} value={col.field}>
																	{col.name}
																</option>
															))}
														</select>
													</td>

													<td className="py-1 border-0" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
														<select
															className="form-select form-select-sm border-secondary-subtle"
															value={filter.operator}
															onChange={(e) =>
																updateFilter(index, {
																	operator: e.target.value as FilterConfig['operator'],
																})
															}
															style={{ fontSize: '0.8rem' }}
														>
															<option value="contains">Contiene el texto</option>
															<option value="equals">Igual a</option>
															<option value="greaterThan">Mayor que (&gt;)</option>
															<option value="lessThan">Menor que (&lt;)</option>
															<option value="between">Entre rango</option>
														</select>
													</td>

													<td className="py-1 border-0" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
														{filter.operator === 'between' ? (
															<div className="d-flex gap-1.5">
																<Input
																	type={isDateField ? 'date' : 'number'}
																	className="form-control-sm border-secondary-subtle flex-fill"
																	placeholder="Desde"
																	value={filter.value || ''}
																	onChange={(e) =>
																		updateFilter(index, {
																			value: e.target.value ? e.target.value : null,
																		})
																	}
																	style={{ fontSize: '0.8rem', width: '50%' }}
																/>
																<Input
																	type={isDateField ? 'date' : 'number'}
																	className="form-control-sm border-secondary-subtle flex-fill"
																	placeholder="Hasta"
																	value={filter.value2 || ''}
																	onChange={(e) =>
																		updateFilter(index, {
																			value2: e.target.value ? e.target.value : null,
																		})
																	}
																	style={{ fontSize: '0.8rem', width: '50%' }}
																/>
															</div>
														) : (
															<Input
																type={
																	isDateField
																		? 'date'
																		: filter.operator === 'greaterThan' ||
																			filter.operator === 'lessThan'
																			? 'number'
																			: 'text'
																}
																className="form-control-sm border-secondary-subtle w-100"
																placeholder={isDateField ? '' : 'Escribe el valor...'}
																value={filter.value || ''}
																onChange={(e) =>
																	updateFilter(index, {
																		value: e.target.value ? e.target.value : null,
																	})
																}
																style={{ fontSize: '0.8rem' }}
															/>
														)}
													</td>

													<td className="py-1 border-0 text-center" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
														<Button
															size="sm"
															color="danger"
															outline
															onClick={() => removeFilter(index)}
															className="d-flex align-items-center justify-content-center mx-auto"
															title="Quitar filtro"
															style={{ height: '31px', width: '31px', padding: '0' }}
														>
															<i className="fa fa-trash-alt" style={{ fontSize: '0.75rem' }}></i>
														</Button>
													</td>
												</tr>
											);
										})}
									</tbody>
								</Table>
							)}
						</div>
					</div>
				</BottomSheet>
			</>
		);
	}

	return (
		<Dropdown isOpen={isOpen} toggle={onToggle} size="sm">
			<DropdownToggle 
				color="none" 
				caret={false}
				className={`d-flex align-items-center btn-sm fw-medium ${activeFiltersCount > 0 ? 'text-white' : 'text-primary'}`}
				style={{
					backgroundColor: activeFiltersCount > 0 ? '#0766ad' : 'rgba(7, 102, 173, 0.06)',
					border: activeFiltersCount > 0 ? '1px solid #0766ad' : '1px solid rgba(7, 102, 173, 0.15)',
					borderRadius: '8px',
					height: '34px',
					padding: '0 12px',
					fontSize: '0.8rem',
					boxShadow: '0 2px 4px rgba(7, 102, 173, 0.04)',
					transition: 'all 0.2s ease',
					fontWeight: 600,
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center'
				}}
				onMouseEnter={(e) => {
					if (activeFiltersCount > 0) {
						e.currentTarget.style.backgroundColor = '#05528a';
					} else {
						e.currentTarget.style.backgroundColor = 'rgba(7, 102, 173, 0.12)';
					}
					e.currentTarget.style.transform = 'translateY(-1px)';
					e.currentTarget.style.boxShadow = '0 4px 8px rgba(7, 102, 173, 0.08)';
				}}
				onMouseLeave={(e) => {
					if (activeFiltersCount > 0) {
						e.currentTarget.style.backgroundColor = '#0766ad';
					} else {
						e.currentTarget.style.backgroundColor = 'rgba(7, 102, 173, 0.06)';
					}
					e.currentTarget.style.transform = 'none';
					e.currentTarget.style.boxShadow = '0 2px 4px rgba(7, 102, 173, 0.04)';
				}}
			>
				<i className={`fa fa-filter me-2 ${activeFiltersCount > 0 ? 'text-white' : 'text-primary'}`} style={{ fontSize: '0.85rem' }}></i>
				Filtros
				{activeFiltersCount > 0 && (
					<Badge color="none" className="ms-2 px-2 py-0.5 rounded-pill" style={{ fontSize: '0.65rem', fontWeight: 700, backgroundColor: '#ffffff', color: '#0766ad' }}>
						{activeFiltersCount}
					</Badge>
				)}
				<i className={`fa fa-chevron-down ms-2 ${activeFiltersCount > 0 ? 'text-white' : 'text-primary'}`} style={{ fontSize: '0.65rem', opacity: 0.8 }}></i>
			</DropdownToggle>
			<DropdownMenu 
				container="body"
				className="advanced-filter-menu"
				onClick={(e) => e.stopPropagation()} 
				style={{ 
					padding: '16px', 
					width: '650px', 
					maxWidth: '90vw', 
					borderRadius: '12px',
					boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
					border: '1px solid rgba(0,0,0,0.06)',
					zIndex: 1000
				}}
				end
			>
				<div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between mb-3 border-bottom pb-2 gap-2">
					<div className="d-flex align-items-center justify-content-between">
						<h6 className="mb-0 fw-bold text-dark d-flex align-items-center" style={{ fontSize: '0.9rem' }}>
							Filtros Avanzados de Búsqueda
						</h6>
						<button
							type="button"
							className="btn-close d-md-none shadow-none"
							onClick={onToggle}
							aria-label="Cerrar"
							style={{ fontSize: '0.7rem', padding: '0.25rem' }}
						/>
					</div>
					
					<div className="d-flex align-items-center justify-content-between justify-content-md-end gap-2 flex-wrap">
						<Button
							size="sm"
							color="secondary"
							outline
							onClick={addFilter}
							disabled={filterableColumns.length === 0}
							className="py-1 px-2 d-flex align-items-center justify-content-center flex-fill flex-md-grow-0"
							style={{ fontSize: '0.75rem' }}
						>
							<i className="fa fa-plus me-2"></i> Agregar
						</Button>
						<Button
							size="sm"
							color="primary"
							onClick={handleApply}
							disabled={filters.length === 0}
							className="py-1 px-2 d-flex align-items-center justify-content-center flex-fill flex-md-grow-0 text-white"
							style={{ 
								fontSize: '0.75rem', 
								color: '#ffffff', 
								opacity: filters.length === 0 ? 0.65 : 1 
							}}
						>
							<span style={{ color: '#ffffff' }} className="d-flex align-items-center text-white justify-content-center">
								<i className="fa fa-check me-2" style={{ color: '#ffffff' }}></i> Aplicar
							</span>
						</Button>
						<Button
							size="sm"
							color="warning"
							outline
							onClick={handleClear}
							className="py-1 px-2 d-flex align-items-center justify-content-center flex-fill flex-md-grow-0"
							style={{ fontSize: '0.75rem' }}
						>
							<i className="fa fa-undo me-2"></i> Limpiar
						</Button>
						<button
							type="button"
							className="btn-close d-none d-md-block ms-2 shadow-none"
							onClick={onToggle}
							aria-label="Cerrar"
							style={{ fontSize: '0.7rem', padding: '0.25rem' }}
						/>
					</div>
				</div>

				<div className="mb-1" style={{ maxHeight: '240px', overflowY: 'auto', overflowX: 'hidden', paddingRight: '4px' }}>
					{filters.length === 0 ? (
						<p className="text-muted small text-center my-3" style={{ fontSize: '0.8rem' }}>
							No hay filtros activos. Haz clic en <strong>Agregar</strong> para comenzar.
						</p>
					) : (
						<Table borderless size="sm" className="align-middle mb-0" style={{ tableLayout: 'fixed', width: '100%' }}>
							<thead className="text-muted fw-bold text-uppercase" style={{ fontSize: '0.7rem', letterSpacing: '0.5px' }}>
								<tr>
									<th className="pb-2 border-0" style={{ width: '25%', paddingLeft: '8px' }}>Campo</th>
									<th className="pb-2 border-0" style={{ width: '25%', paddingLeft: '8px' }}>Condición</th>
									<th className="pb-2 border-0" style={{ width: '42%', paddingLeft: '8px' }}>Valor / Rango</th>
									<th className="pb-2 border-0 text-center" style={{ width: '8%' }}></th>
								</tr>
							</thead>
							<tbody>
								{filters.map((filter, index) => {
									const selectedCol = filterableColumns.find((c) => c.field === filter.field);
									const isDateField = selectedCol 
										? selectedCol.field.toLowerCase().includes('date') || selectedCol.name.toLowerCase().includes('fecha')
										: false;

									return (
										<tr key={index}>
											<td className="py-1 border-0" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
												<select
													className="form-select form-select-sm border-secondary-subtle"
													value={filter.field}
													onChange={(e) => updateFilter(index, { field: e.target.value })}
													style={{ fontSize: '0.8rem' }}
												>
													{filterableColumns.map((col) => (
														<option key={col.field} value={col.field}>
															{col.name}
														</option>
													))}
												</select>
											</td>

											<td className="py-1 border-0" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
												<select
													className="form-select form-select-sm border-secondary-subtle"
													value={filter.operator}
													onChange={(e) =>
														updateFilter(index, {
															operator: e.target.value as FilterConfig['operator'],
														})
													}
													style={{ fontSize: '0.8rem' }}
												>
													<option value="contains">Contiene el texto</option>
													<option value="equals">Igual a</option>
													<option value="greaterThan">Mayor que (&gt;)</option>
													<option value="lessThan">Menor que (&lt;)</option>
													<option value="between">Entre rango</option>
												</select>
											</td>

											<td className="py-1 border-0" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
												{filter.operator === 'between' ? (
													<div className="d-flex gap-1.5">
														<Input
															type={isDateField ? 'date' : 'number'}
															className="form-control-sm border-secondary-subtle flex-fill"
															placeholder="Desde"
															value={filter.value || ''}
															onChange={(e) =>
																updateFilter(index, {
																	value: e.target.value ? e.target.value : null,
																})
															}
															style={{ fontSize: '0.8rem', width: '50%' }}
														/>
														<Input
															type={isDateField ? 'date' : 'number'}
															className="form-control-sm border-secondary-subtle flex-fill"
															placeholder="Hasta"
															value={filter.value2 || ''}
															onChange={(e) =>
																updateFilter(index, {
																	value2: e.target.value ? e.target.value : null,
																})
															}
															style={{ fontSize: '0.8rem', width: '50%' }}
														/>
													</div>
												) : (
													<Input
														type={
															isDateField
																? 'date'
																: filter.operator === 'greaterThan' ||
																	filter.operator === 'lessThan'
																	? 'number'
																	: 'text'
														}
														className="form-control-sm border-secondary-subtle w-100"
														placeholder={isDateField ? '' : 'Escribe el valor...'}
														value={filter.value || ''}
														onChange={(e) =>
															updateFilter(index, {
																value: e.target.value ? e.target.value : null,
															})
														}
														style={{ fontSize: '0.8rem' }}
													/>
												)}
											</td>

											<td className="py-1 border-0 text-center" style={{ paddingLeft: '4px', paddingRight: '4px' }}>
												<Button
													size="sm"
													color="danger"
													outline
													onClick={() => removeFilter(index)}
													className="d-flex align-items-center justify-content-center mx-auto"
													title="Quitar filtro"
													style={{ height: '31px', width: '31px', padding: '0' }}
												>
													<i className="fa fa-trash-alt" style={{ fontSize: '0.75rem' }}></i>
												</Button>
											</td>
										</tr>
									);
								})}
							</tbody>
						</Table>
					)}
				</div>
			</DropdownMenu>
		</Dropdown>
	);
};
